import { Box, Center, Fade, Flex, Heading, Spinner } from '@chakra-ui/react';
import gql from 'graphql-tag';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MasterClassWhereInput,
  MeetingWhereInput,
  useDashboardViewQuery,
} from '@/@generated/codegen/API';
import { useLoggedInUserAuthContext } from '@/components/AuthTokenContextProvider/UserAuthContext';
import { LoadingDot } from '@/components/LoadingDot/LoadingDot';
import { MasterClassListClient } from '@/components/MasterClassList/MasterClassListClient';
import MeetingListClient from '@/components/MeetingList/MeetingListClient';
import { useEntitySubscription } from '@/hooks/useEntitySubscription';
import PageLayout from '@/ui/Layout/PageLayout';
import { captureSentry } from '@/utils/helpers/sentryHelper';

import useCypressScreenshotRedirect from './hook/useCypressScreenshotRedirect';

gql`
  query DashboardView(
    $paginationArgs: PaginationArgs!
    $where: MeetingWhereInput
    $clientsPaginationArgs: PaginationArgs
    $masterClassPaginationArgs: PaginationArgs
    $masterClassWhere: MasterClassWhereInput
    # $accessLinkPaginationArgs: PaginationArgs
    $patientCasesPaginationArgs: PaginationArgs
  ) {
    listMeetings(paginationArgs: $paginationArgs, where: $where) {
      edges {
        node {
          ...MeetingFields
          ...MeetingRelationCaseCluster
          ...MeetingRelationHost
          ...MeetingRelationClients
          # NOTE: No need for this since we fetch them seperately inside the menu
          # ...MeetingRelationAccessLink
          ...MeetingRelationMeetingStats
          ...MeetingRelationHostFields
        }
      }
    }
    listMasterClasses(paginationArgs: $masterClassPaginationArgs, where: $masterClassWhere) {
      edges {
        node {
          ...MasterClassFields
          ...MasterClassRelationPatientCases
        }
      }
    }
  }
`;

const meetingwhere: MeetingWhereInput = {
  isArchived: {
    equals: false,
  },
  // NOTE: we do not want to show ended meetings
  OR: [
    {
      endDate: {
        equals: null,
      },
    },
    {
      endDate: {
        gte: new Date(),
      },
    },
  ],
};

const masterClasswhere: MasterClassWhereInput = {
  // TODO: add isArchived here when it's implemented
};

function HomeView() {
  const { userID } = useLoggedInUserAuthContext();
  const { t } = useTranslation();
  const query = useDashboardViewQuery({
    variables: {
      paginationArgs: {
        first: 100,
      },
      where: meetingwhere,
    },
  });

  const meetingList = useMemo(() => {
    return query.data?.listMeetings.edges.map((edge) => edge.node) || [];
  }, [query.data]);

  const masterClassList = useMemo(() => {
    return query.data?.listMasterClasses.edges.map((edge) => edge.node) || [];
  }, [query.data]);

  useEntitySubscription('Meeting', {
    onCreate: {
      variables: {
        where: meetingwhere,
      },
      onData: () => {
        void query.refetch();
      },
    },
    onUpdate: {
      variables: {
        where: meetingwhere,
      },
      onData: () => {
        void query.refetch();
      },
    },
    onDelete: {
      variables: {},
      onData: () => {
        void query.refetch();
      },
    },
  });

  useEntitySubscription('MasterClass', {
    onCreate: {
      variables: {
        where: masterClasswhere,
      },
      onData: () => {
        void query.refetch();
      },
    },
    onUpdate: {
      variables: {
        where: masterClasswhere,
      },
      onData: () => {
        void query.refetch();
      },
    },
    onDelete: {
      variables: {},
      onData: () => {
        void query.refetch();
      },
    },
  });

  // NOTE: user could have received access while on this page, so we need to refetch the query
  useEntitySubscription('ExplicitUserAccess', {
    onCreate: {
      variables: {
        where: {
          userId: {
            equals: userID,
          },
        },
      },
      onData: ({ data }) => {
        const obj = data.data?.onCreateExplicitUserAccess;
        if (!obj) {
          captureSentry({
            title:
              'AdminExplicitUserAccessListClient | onData | onCreateExplicitUserAccess | obj is undefined',
            detail: {},
          });
          return;
        }
        void query.refetch();
      },
    },
    onDelete: {
      variables: {},
      onData: ({ data }) => {
        const obj = data.data?.onDeleteExplicitUserAccess;
        if (!obj) {
          captureSentry({
            title:
              'AdminExplicitUserAccessListClient | onData | onDeleteExplicitUserAccess | obj is undefined',
            detail: {},
          });
          return;
        }
        void query.refetch();
      },
    },
  });
  // TODO/NTH: I would also need a "addedToTeam and removedFromTeam" + same for clients subscription
  // since this impacts if they have access to certain meetings or not

  useCypressScreenshotRedirect();

  const hasMasterClasses = masterClassList.length > 0;
  const hasMeetings = meetingList.length > 0;

  console.log('DEBUG >> v.h');

  return (
    <PageLayout>
      <Flex w="full" h="full">
        {!hasMeetings && !hasMasterClasses && (
          <Box p="20px" bg="livelinx.grey100" h="100%" w="full">
            <Fade in={true}>
              <Box h="full">
                <Center h="100px">
                  {query.loading ? (
                    <Spinner />
                  ) : (
                    <Heading size="sm" color="grey">
                      {t('home.nothingToShow')}
                      <LoadingDot />
                    </Heading>
                  )}
                </Center>
              </Box>
            </Fade>
          </Box>
        )}
        {hasMeetings && <MeetingListClient title="Meetings" meetingList={meetingList} />}
        {hasMasterClasses && (
          <MasterClassListClient title="MasterClasses" masterClassList={masterClassList} />
        )}
      </Flex>
    </PageLayout>
  );
}

export default HomeView;
